import type { Component } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { isColorScheme } from "@/types/color-scheme";
import { normalizeLegacySemanticStyleColor } from "@/utils/theme-aware-legacy-colors";

export type StyleBreakpoint = "mobile" | "tablet" | "desktop";
export type RenderableStyleValue = string | number;
export type RenderableStyle = Record<string, RenderableStyleValue>;
export type RenderableConstraints = Record<string, unknown>;
export type ComponentSchemaVersion = 1 | 2;

export interface SchemaAwareStyleUpdateOptions {
  projectSchemaVersion?: ComponentSchemaVersion;
  breakpoint?: unknown;
  styleWriteTarget?: unknown;
}

const BREAKPOINT_KEYS = ["mobile", "tablet", "desktop"] as const;
const BREAKPOINT_STYLE_SLOT_KEYS = ["base", ...BREAKPOINT_KEYS] as const;
const COLOR_SCHEME_STYLE_SLOT_KEYS = ["dark", "light"] as const;
const FORBIDDEN_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);

export function getRenderableComponentStyle(
  component: { id?: unknown; name?: unknown; style?: unknown; type?: unknown; parentId?: unknown },
  activeBreakpoint: StyleBreakpoint,
  activeColorScheme?: ColorScheme,
): RenderableStyle {
  const style = getRecord(component.style);
  if (!style) return {};

  if (!hasV2StyleSlot(style)) {
    return copyRenderableStyle(style, component, activeColorScheme !== undefined);
  }

  return {
    ...copyRenderableStyle(getRecord(style.base), component, activeColorScheme !== undefined),
    ...(activeColorScheme
      ? copyRenderableStyle(getRecord(style[activeColorScheme]), component, true)
      : {}),
    ...copyRenderableStyle(getRecord(style[activeBreakpoint]), component, activeColorScheme !== undefined),
  };
}

export function withNormalizedComponentStyle(
  component: Component,
  activeBreakpoint: StyleBreakpoint,
  activeColorScheme?: ColorScheme,
): Component {
  return {
    ...component,
    style: getRenderableComponentStyle(component, activeBreakpoint, activeColorScheme),
  };
}

export function applySchemaAwareStyleUpdate(
  component: { style?: unknown },
  styleUpdates: Record<string, unknown>,
  options: SchemaAwareStyleUpdateOptions = {},
): Record<string, unknown> {
  const currentStyle = getRecord(component.style);
  const isV2 = options.projectSchemaVersion === 2 || (currentStyle ? hasV2StyleSlot(currentStyle) : false);

  if (!isV2) {
    return cleanupShorthandConflicts(
      applySafeStyleUpdates(copySafeRecord(currentStyle), styleUpdates),
      styleUpdates,
    );
  }

  let nextStyle: Record<string, unknown> = {
    base: copySafeRecord(getRecord(currentStyle?.base)),
    mobile: copySafeRecord(getRecord(currentStyle?.mobile)),
    tablet: copySafeRecord(getRecord(currentStyle?.tablet)),
    desktop: copySafeRecord(getRecord(currentStyle?.desktop)),
    dark: copySafeRecord(getRecord(currentStyle?.dark)),
    light: copySafeRecord(getRecord(currentStyle?.light)),
  };

  const targetSlot = getStyleTargetSlot(options.breakpoint, options.styleWriteTarget);
  const flatUpdates: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(styleUpdates)) {
    if (!isSafeObjectKey(key)) continue;

    if (isStyleSlotKey(key)) {
      const slotUpdates = getRecord(value);
      if (!slotUpdates) continue;
      nextStyle[key] = cleanupShorthandConflicts(
        applySafeStyleUpdates(copySafeRecord(getRecord(nextStyle[key])), slotUpdates),
        slotUpdates,
      );
    } else {
      flatUpdates[key] = value;
    }
  }

  if (Object.keys(flatUpdates).length > 0) {
    nextStyle[targetSlot] = cleanupShorthandConflicts(
      applySafeStyleUpdates(copySafeRecord(getRecord(nextStyle[targetSlot])), flatUpdates),
      flatUpdates,
    );
  }

  nextStyle = cleanupOptionalColorSchemeSlots(nextStyle, currentStyle);
  return nextStyle;
}

export function hasSchemaAwareStyleProperty(
  component: { style?: unknown },
  property: string,
  options: Pick<SchemaAwareStyleUpdateOptions, "projectSchemaVersion"> = {},
): boolean {
  if (!isSafeObjectKey(property)) return false;

  const style = getRecord(component.style);
  if (!style) return false;

  const isV2 = options.projectSchemaVersion === 2 || hasV2StyleSlot(style);
  if (!isV2) {
    return style[property] !== undefined;
  }

  return ALL_STYLE_SLOT_KEYS.some(slotKey => {
    const slot = getRecord(style[slotKey]);
    return slot?.[property] !== undefined;
  });
}

export function hasV2ComponentStyleSlots(component: { style?: unknown }): boolean {
  const style = getRecord(component.style);
  return style ? hasV2StyleSlot(style) : false;
}

export function getStylePropertyUpdateValue(
  styleUpdates: Record<string, unknown> | undefined,
  property: string,
  breakpoint?: unknown,
): unknown {
  if (!styleUpdates || !isSafeObjectKey(property)) return undefined;

  const targetSlot = getStyleTargetSlot(breakpoint);
  const targetUpdates = getRecord(styleUpdates[targetSlot]);
  if (targetUpdates?.[property] !== undefined) return targetUpdates[property];

  return styleUpdates[property];
}

export function getRenderableResponsiveConstraints(
  component: { responsivePosition?: unknown },
  activeBreakpoint: StyleBreakpoint,
): RenderableConstraints {
  const responsivePosition = getRecord(component.responsivePosition);
  const constraints = getRecord(responsivePosition?.constraints);
  if (!constraints) return {};

  if (!hasV2ConstraintSlot(constraints)) {
    return copySafeRecord(constraints);
  }

  return {
    ...copySafeRecord(getRecord(constraints.base)),
    ...copySafeRecord(getRecord(constraints[activeBreakpoint])),
  };
}

function hasV2StyleSlot(style: Record<string, unknown>): boolean {
  return ALL_STYLE_SLOT_KEYS.some(key => getRecord(style[key]) !== undefined);
}

function hasV2ConstraintSlot(constraints: Record<string, unknown>): boolean {
  return BREAKPOINT_STYLE_SLOT_KEYS.some(key => getRecord(constraints[key]) !== undefined);
}

function getStyleTargetSlot(
  breakpoint: unknown,
  styleWriteTarget?: unknown,
): "base" | StyleBreakpoint | ColorScheme {
  if (isStyleBreakpoint(breakpoint)) return breakpoint;
  return isColorScheme(styleWriteTarget) ? styleWriteTarget : "base";
}

function isStyleBreakpoint(value: unknown): value is StyleBreakpoint {
  return typeof value === "string" && BREAKPOINT_KEYS.some(key => key === value);
}

const ALL_STYLE_SLOT_KEYS = [
  ...BREAKPOINT_STYLE_SLOT_KEYS,
  ...COLOR_SCHEME_STYLE_SLOT_KEYS,
] as const;

function isStyleSlotKey(value: string): value is typeof ALL_STYLE_SLOT_KEYS[number] {
  return ALL_STYLE_SLOT_KEYS.some(key => key === value);
}

function cleanupOptionalColorSchemeSlots(
  style: Record<string, unknown>,
  previousStyle: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const slotsToOmit = COLOR_SCHEME_STYLE_SLOT_KEYS.filter((slot) => {
    const previousSlot = getRecord(previousStyle?.[slot]);
    const nextSlot = getRecord(style[slot]);
    return !previousSlot && nextSlot && Object.keys(nextSlot).length === 0;
  });

  if (slotsToOmit.length === 0) return style;

  const omittedSlots = new Set<string>(slotsToOmit);
  return Object.fromEntries(
    Object.entries(style).filter(([key]) => !omittedSlots.has(key)),
  );
}

function omitKeys(
  record: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> {
  if (keys.length === 0) return record;

  const omittedKeys = new Set<string>(keys);
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => !omittedKeys.has(key)),
  );
}

function cleanupShorthandConflicts(
  style: Record<string, unknown>,
  updates: Record<string, unknown>,
): Record<string, unknown> {
  let nextStyle = style;

  const hasShorthandPadding = updates.padding !== undefined && updates.padding !== null;
  const hasIndividualPadding =
    updates.paddingTop !== undefined ||
    updates.paddingRight !== undefined ||
    updates.paddingBottom !== undefined ||
    updates.paddingLeft !== undefined;

  if (hasShorthandPadding) {
    nextStyle = omitKeys(nextStyle, ["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"]);
  } else if (hasIndividualPadding) {
    nextStyle = omitKeys(nextStyle, ["padding"]);
  }

  const hasShorthandMargin = updates.margin !== undefined && updates.margin !== null;
  const hasIndividualMargin =
    updates.marginTop !== undefined ||
    updates.marginRight !== undefined ||
    updates.marginBottom !== undefined ||
    updates.marginLeft !== undefined;

  if (hasShorthandMargin) {
    nextStyle = omitKeys(nextStyle, ["marginTop", "marginRight", "marginBottom", "marginLeft"]);
  } else if (hasIndividualMargin) {
    nextStyle = omitKeys(nextStyle, ["margin"]);
  }

  return nextStyle;
}

function applySafeStyleUpdates(
  style: Record<string, unknown>,
  updates: Record<string, unknown>,
): Record<string, unknown> {
  let nextStyle = style;

  for (const [key, value] of Object.entries(updates)) {
    if (!isSafeObjectKey(key)) continue;

    if (value === undefined) {
      nextStyle = omitKeys(nextStyle, [key]);
    } else {
      nextStyle = {
        ...nextStyle,
        [key]: value,
      };
    }
  }

  return nextStyle;
}

function copyRenderableStyle(
  source: Record<string, unknown> | undefined,
  component: { id?: unknown; name?: unknown; type?: unknown; parentId?: unknown },
  normalizeLegacyColors: boolean,
): RenderableStyle {
  if (!source) return {};

  const result: RenderableStyle = {};
  for (const [key, value] of Object.entries(source)) {
    if (!isSafeObjectKey(key)) continue;

    const renderableValue = toRenderableStyleValue(value);
    if (renderableValue !== undefined) {
      result[key] =
        normalizeLegacyColors && typeof renderableValue === "string"
          ? normalizeLegacySemanticStyleColor(key, renderableValue, component)
          : renderableValue;
    }
  }
  return result;
}

function copySafeRecord(source: Record<string, unknown> | undefined): RenderableConstraints {
  if (!source) return {};

  const result: RenderableConstraints = {};
  for (const [key, value] of Object.entries(source)) {
    if (isSafeObjectKey(key) && value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

function toRenderableStyleValue(value: unknown): RenderableStyleValue | undefined {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  const tokenRef = getTokenReference(value);
  return tokenRef ? `var(--${tokenRef})` : undefined;
}

function getTokenReference(value: unknown): string | undefined {
  const record = getRecord(value);
  if (!record || typeof record.$value !== "string") return undefined;

  const normalized = record.$value.trim().replace(/\./g, "-");
  return /^[A-Za-z0-9_-]+$/.test(normalized) ? normalized : undefined;
}

function getRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function isSafeObjectKey(key: string): boolean {
  return !FORBIDDEN_OBJECT_KEYS.has(key);
}
